import * as exerciseApi from "./exercise.api";
import * as answerApi from "./student-answers.api";
import * as progressApi from "./module-progress.api";
import config from "../../config/config";
import {
  Exercise,
  StudentExerciseAnswer,
  StudentModuleProgress,
} from "./exercise.interfaces";

/**
 * Dados completos do módulo carregados serialmente
 */
export interface ModuleExercisesData {
  exercises: Exercise[];
  answers: StudentExerciseAnswer[];
  progress: StudentModuleProgress;
}

/**
 * Carrega dados do módulo de forma SERIAL:
 * 1. Exercícios
 * 2. Respostas do estudante
 * 3. Progresso/relatório
 * 
 * @param moduleId ID do módulo
 * @returns Dados do módulo com exercícios, respostas e progresso
 */
export const loadModuleExercisesData = async (
  moduleId: string
): Promise<ModuleExercisesData> => {
  try {
    if (config.useRealApi) {
      // Passo 1: Carrega exercícios
      console.log(`[Serial Load] Step 1/3: Loading exercises for module ${moduleId}...`);
      const exercises = await exerciseApi.apiFetchExercisesByModule(moduleId);
      console.log(`[Serial Load] ✓ Loaded ${exercises.length} exercises`);

      // Passo 2: Carrega respostas do estudante
      console.log(`[Serial Load] Step 2/3: Loading student answers for module ${moduleId}...`);
      const answers = await answerApi.apiGetStudentExerciseAnswers(moduleId);
      console.log(`[Serial Load] ✓ Loaded ${answers.length} answers`);

      // Passo 3: Carrega progresso/relatório
      console.log(`[Serial Load] Step 3/3: Loading progress report for module ${moduleId}...`);
      const progress = await progressApi.apiGetModuleProgress(moduleId);
      console.log(`[Serial Load] ✓ Loaded progress: ${progress.progressPercentage}%`);

      return { exercises, answers, progress };
    } else {
      // Fallback para mocks (se config.useRealApi === false)
      // TODO: Implementar mocks quando necessário
      throw new Error("Mock implementation not yet available. Set config.useRealApi to true.");
    }
  } catch (error) {
    console.error("Error loading module exercises data:", error);
    throw error;
  }
};

/**
 * Avalia exercício e depois busca novo progresso
 * 
 * @param request Dados da avaliação
 * @returns Resposta da avaliação e novo progresso
 */
export const evaluateExerciseAndRefreshProgress = async (
  request: {
    exerciseId: string;
    moduleId: string;
    answer: any;
    exerciseType: string;
  }
): Promise<{
  evaluation: import("./exercise.interfaces").EvaluateExerciseResponse;
  progress: StudentModuleProgress;
}> => {
  try {
    if (config.useRealApi) {
      // 1. Primeiro: Avalia o exercício
      console.log(`[Evaluate] Step 1/2: Evaluating exercise ${request.exerciseId}...`);
      const evaluation = await exerciseApi.apiEvaluateExerciseAnswer({
        exerciseId: request.exerciseId,
        moduleId: request.moduleId,
        answer: request.answer,
        exerciseType: request.exerciseType,
      });
      console.log(`[Evaluate] ✓ Exercise evaluated: ${evaluation.isCorrect ? "Correct" : "Incorrect"}`);

      // 2. Depois: Busca novo progresso/status
      console.log(`[Evaluate] Step 2/2: Fetching updated progress for module ${request.moduleId}...`);
      const progress = await progressApi.apiGetModuleProgress(request.moduleId);
      console.log(`[Evaluate] ✓ Progress updated: ${progress.progressPercentage}%`);

      return { evaluation, progress };
    } else {
      // Fallback para mocks
      throw new Error("Mock implementation not yet available. Set config.useRealApi to true.");
    }
  } catch (error) {
    console.error("Error evaluating exercise and refreshing progress:", error);
    throw error;
  }
};

